export type CohortMethod = 'prev' | 'avg' | 'wavg' | 'chain' | 'seasonal' | 'fixed';

export interface CohortInputRule {
  method: CohortMethod;
  n?: number;
  value?: number;
  season?: boolean;
  trend?: boolean;
}

export type CohortInputMap = Record<string, CohortInputRule>;

export type CohortCellKind = 'actual' | 'override' | 'birth' | 'rule' | 'fallback';

export interface CohortForecastCohort {
  label: string | number;
  ratio?: Array<number | null | undefined> | null;
}

export interface CohortForecastInput {
  months: number[];
  cut: number;
  cohorts: CohortForecastCohort[];
  sizes: Array<number | null | undefined>;
  override?: (j: number, t: number) => number | null | undefined;
  ruleFor: (label: string) => CohortInputRule;
  explain?: boolean;
}

export interface CohortSourceTerm {
  j: number;
  t: number;
  rate: number;
  weight?: number;
}

export interface CohortCellRef {
  j: number;
  t: number;
}

export interface CohortCellSource {
  method: CohortMethod | 'fallback' | 'carry' | 'own';
  rule?: CohortInputRule;
  terms: CohortSourceTerm[];
  factor?: { value: number; num: number; den: number; cohorts: number; fromCurve: boolean; cells: CohortCellRef[] } | null;
  trend?: { value: number; num: number; den: number; pairs: number; capped: boolean; cells: CohortCellRef[] } | null;
  curve?: { age: number; cohorts: number; cells: CohortCellRef[] } | null;
  result: number;
}

export interface CohortForecastResult {
  rates: Array<Array<number | null>>;
  kinds: Array<Array<CohortCellKind | null>>;
  sources?: Array<Array<CohortCellSource | null>>;
}

export const COHORT_METHODS: CohortMethod[] = ['prev', 'avg', 'wavg', 'chain', 'seasonal', 'fixed'];

export const COHORT_METHOD_LABEL: Record<CohortMethod, string> = {
  prev: 'Previous pattern',
  avg: 'Average of last N',
  wavg: 'Weighted average of last N',
  chain: 'Continue own curve',
  seasonal: 'Seasonal with trend',
  fixed: 'Fixed %'
};

export const COHORT_N_LIMITS: Record<Exclude<CohortMethod, 'fixed'>, [number, number]> = {
  prev: [1, 36],
  avg: [1, 36],
  wavg: [1, 36],
  chain: [1, 36],
  seasonal: [1, 12]
};

const DEFAULT_N: Record<Exclude<CohortMethod, 'fixed'>, number> = { prev: 1, avg: 3, wavg: 3, chain: 6, seasonal: 3 };

export const DEFAULT_FIXED_RATE = 0.1;

export const DEFAULT_COHORT_RULE: CohortInputRule = { method: 'prev', n: 1 };

const SEASON = 12;
const TREND_MIN = 0.5;
const TREND_MAX = 2;

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export function isCohortMethod(v: unknown): v is CohortMethod {
  return typeof v === 'string' && (COHORT_METHODS as string[]).indexOf(v) >= 0;
}

export function cohortInputKey(ch: string, blk: string, label?: string | number | null): string {
  return label === null || label === undefined || label === '' ? ch + '|' + blk : ch + '|' + blk + '|' + String(label);
}

export function normalizeCohortRule(raw: unknown): CohortInputRule | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { method?: unknown; n?: unknown; value?: unknown; season?: unknown; trend?: unknown };
  const method = typeof r.method === 'string' ? r.method.trim().toLowerCase() : '';
  if (!isCohortMethod(method)) return null;
  if (method === 'fixed') {
    const value = Number(r.value);
    return isNum(value) && value >= 0 && value <= 1 ? { method, value } : null;
  }
  const n = Number(r.n);
  const [min, max] = COHORT_N_LIMITS[method];
  if (!Number.isInteger(n) || n < min || n > max) return null;
  const rule: CohortInputRule = { method, n };
  if (method !== 'seasonal' && r.season === true) rule.season = true;
  if (method !== 'seasonal' && method !== 'chain' && r.trend === true) rule.trend = true;
  return rule;
}

export function sanitizeCohortInput(raw: unknown): CohortInputMap {
  const out: CohortInputMap = {};
  if (!raw || typeof raw !== 'object') return out;
  Object.keys(raw as Record<string, unknown>).forEach(key => {
    const parts = key.split('|');
    if (parts.length < 2 || parts.length > 3 || parts.some(p => !p)) return;
    const rule = normalizeCohortRule((raw as Record<string, unknown>)[key]);
    if (rule) out[key] = rule;
  });
  return out;
}

export function resolveCohortRule(map: CohortInputMap | null | undefined, ch: string, blk: string, label?: string | number | null): CohortInputRule {
  const m = map || {};
  if (label !== null && label !== undefined && label !== '') {
    const own = m[cohortInputKey(ch, blk, label)];
    if (own) return own;
  }
  return m[cohortInputKey(ch, blk)] || DEFAULT_COHORT_RULE;
}

export function defaultRuleFor(method: CohortMethod, current?: CohortInputRule | null): CohortInputRule {
  if (method === 'fixed') {
    return { method, value: current && current.method === 'fixed' && isNum(current.value) ? current.value : DEFAULT_FIXED_RATE };
  }
  const [min, max] = COHORT_N_LIMITS[method];
  const n = current && isNum(current.n) && current.n >= min && current.n <= max ? current.n : DEFAULT_N[method];
  return { method, n };
}

export function sameCohortRule(a: CohortInputRule | null | undefined, b: CohortInputRule | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  return a.method === b.method && (a.n ?? null) === (b.n ?? null) && (a.value ?? null) === (b.value ?? null) && !!a.season === !!b.season && !!a.trend === !!b.trend;
}

const pctText = (v: number | undefined) => (isNum(v) ? String(Math.round(v * 1000) / 10) : '0') + '%';

export type PickerMethod = 'wavg' | 'chain' | 'avg' | 'prev';

export const PICKER_MAIN: PickerMethod[] = ['wavg', 'chain'];
export const PICKER_OTHER: PickerMethod[] = ['avg', 'prev'];
export const PICKER_METHODS: PickerMethod[] = PICKER_MAIN.concat(PICKER_OTHER);
export const RECENT_N_LIMITS: [number, number] = [2, 6];

export const COHORT_SCREEN_NAME: Record<CohortMethod, string> = {
  wavg: 'Recent cohorts weighted average',
  chain: 'Latest actual + typical decay',
  avg: 'Recent cohorts average',
  prev: 'Copy one earlier cohort',
  seasonal: 'Seasonal with trend',
  fixed: 'Fixed percentage'
};

export const COHORT_CHIP_NAME: Record<CohortMethod, string> = {
  wavg: 'Weighted',
  chain: 'Decay',
  avg: 'Average',
  prev: 'Copy',
  seasonal: 'Seasonal',
  fixed: 'Fixed'
};

export const COHORT_METHOD_TIP: Record<PickerMethod, string> = {
  wavg: 'Uses the last few cohorts at the same age, with bigger cohorts counting more. The most reliable choice for most models.',
  chain: 'Starts each cohort from its own latest actual repeat rate, then applies the typical month to month decay of recent cohorts.',
  avg: 'Uses the last few cohorts at the same age, each counting the same. Small cohorts can pull the result around.',
  prev: 'Copies what one earlier cohort did at the same age. One unusual month repeats straight into the forecast.'
};

export function isPickerMethod(v: unknown): v is PickerMethod {
  return typeof v === 'string' && (PICKER_METHODS as string[]).indexOf(v) >= 0;
}

export function migrateCohortRule(rule: CohortInputRule): CohortInputRule {
  return rule.method === 'seasonal' ? { method: 'wavg', n: rule.n, season: true, trend: true } : rule;
}

const switchText = (rule: CohortInputRule) => (rule.season ? ' + Monthly seasonality' : '') + (rule.trend ? ' + Year on year trend' : '');

function countText(rule: CohortInputRule): string {
  if (rule.method === 'prev') return rule.n + (rule.n === 1 ? ' month back' : ' months back');
  return rule.n + (rule.n === 1 ? ' cohort' : ' cohorts');
}

export function describeCohortChoice(rule: CohortInputRule): string {
  return COHORT_SCREEN_NAME[rule.method] + switchText(rule);
}

export function describeCohortRule(rule: CohortInputRule): string {
  if (rule.method === 'fixed') return COHORT_SCREEN_NAME.fixed + ' ' + pctText(rule.value);
  return COHORT_SCREEN_NAME[rule.method] + ' (' + countText(rule) + ')' + switchText(rule);
}

export function describeCohortRuleFull(rule: CohortInputRule): string {
  if (rule.method === 'fixed') return describeCohortRule(rule);
  return [
    COHORT_SCREEN_NAME[rule.method],
    countText(rule),
    'Monthly seasonality ' + (rule.season ? 'on' : 'off'),
    'Year on year trend ' + (rule.trend ? 'on' : 'off')
  ].join(', ');
}

export const NEWER_COHORTS = 'Newer cohorts';
export const OLDER_COHORTS = 'Older cohorts: ' + COHORT_SCREEN_NAME.chain;

export function describeSplitRule(rule: CohortInputRule): string {
  if (rule.method === 'fixed') return describeCohortRule(rule);
  return NEWER_COHORTS + ': ' + COHORT_SCREEN_NAME[rule.method] + ' (' + countText(rule) + ') · ' + OLDER_COHORTS + ' · Monthly seasonality ' + (rule.season ? 'on' : 'off');
}

export function cohortRuleTag(rule: CohortInputRule): string {
  return COHORT_CHIP_NAME[rule.method] + ' ' + (rule.method === 'fixed' ? pctText(rule.value) : rule.n);
}

export function parseCohortYm(label: string | number | null | undefined): number {
  if (label === null || label === undefined) return NaN;
  if (typeof label === 'number') return label;
  const s = String(label).trim();
  if (/^\d{6}$/.test(s)) return +s;
  const m = /^(\d{4})[-/]?(\d{1,2})$/.exec(s);
  return m ? (+m[1]) * 100 + (+m[2]) : NaN;
}

export function forecastCohortRates(input: CohortForecastInput): CohortForecastResult {
  const { months, cut, cohorts, sizes, override, ruleFor } = input;
  const n = months.length;
  const J = cohorts.length;
  const rates: Array<Array<number | null>> = cohorts.map(() => new Array(n).fill(null));
  const kinds: Array<Array<CohortCellKind | null>> = cohorts.map(() => new Array(n).fill(null));
  const isPre = cohorts.map(c => /^pre/i.test(String(c.label)));
  const birth = cohorts.map((c, j) => (isPre[j] ? -1 : months.indexOf(parseCohortYm(c.label))));
  const byBirth: Record<number, number> = {};
  birth.forEach((b, j) => { if (b >= 0 && byBirth[b] === undefined) byBirth[b] = j; });
  const weight = (j: number) => (isNum(sizes[j]) && (sizes[j] as number) > 0 ? (sizes[j] as number) : 1);

  for (let j = 0; j < J; j++) {
    const ratio = cohorts[j].ratio || [];
    for (let t = 0; t < n; t++) {
      const ov = override && t > cut ? override(j, t) : undefined;
      if (isNum(ov)) { rates[j][t] = ov; kinds[j][t] = 'override'; continue; }
      if (!isPre[j] && (birth[j] < 0 || t < birth[j])) continue;
      if (t <= cut) {
        const v = ratio[t];
        if (isNum(v)) { rates[j][t] = v; kinds[j][t] = 'actual'; }
      } else if (!isPre[j] && t === birth[j]) {
        rates[j][t] = 1;
        kinds[j][t] = 'birth';
      }
    }
  }

  const curveNum: number[] = [];
  const curveDen: number[] = [];
  const curveCells: CohortCellRef[][] = [];
  for (let j = 0; j < J; j++) {
    if (isPre[j] || birth[j] < 0) continue;
    for (let t = birth[j]; t <= cut && t < n; t++) {
      if (kinds[j][t] !== 'actual') continue;
      const a = t - birth[j];
      curveNum[a] = (curveNum[a] || 0) + weight(j) * (rates[j][t] as number);
      curveDen[a] = (curveDen[a] || 0) + weight(j);
      (curveCells[a] = curveCells[a] || []).push({ j, t });
    }
  }
  const curvePoint = (a: number): { age: number; value: number; cohorts: number; cells: CohortCellRef[] } | null => {
    for (let x = Math.min(a, curveDen.length - 1); x >= 0; x--) {
      if (curveDen[x]) return { age: x, value: curveNum[x] / curveDen[x], cohorts: curveCells[x].length, cells: curveCells[x] };
    }
    return null;
  };
  type Factor = { value: number; num: number; den: number; cohorts: number; fromCurve: boolean; cells: CohortCellRef[] } | null;
  const newestFirst = cohorts.map((_, j) => j).filter(j => !isPre[j] && birth[j] >= 0).sort((x, y) => birth[y] - birth[x]);
  const factorCache: Record<string, Factor> = {};
  const chainFactor = (a: number, count: number): Factor => {
    const key = a + '|' + count;
    if (factorCache[key] !== undefined) return factorCache[key];
    let num = 0, den = 0, used = 0;
    const cells: CohortCellRef[] = [];
    for (const j of newestFirst) {
      if (used >= count) break;
      const t = birth[j] + a;
      if (t > cut || t >= n || kinds[j][t] !== 'actual' || kinds[j][t - 1] !== 'actual') continue;
      num += rates[j][t] as number;
      den += rates[j][t - 1] as number;
      cells.push({ j, t }, { j, t: t - 1 });
      used++;
    }
    let f: Factor = den > 0 ? { value: num / den, num, den, cohorts: used, fromCurve: false, cells } : null;
    if (f === null) {
      const hi = curvePoint(a), lo = curvePoint(a - 1);
      f = hi && lo && lo.value > 0
        ? { value: hi.value / lo.value, num: hi.value, den: lo.value, cohorts: 0, fromCurve: true, cells: hi.cells.concat(lo.cells) }
        : null;
    }
    factorCache[key] = f;
    return f;
  };

  const at = (b: number, t: number): number | null => {
    if (b < 0 || t < 0) return null;
    const j = byBirth[b];
    return j === undefined ? null : rates[j][t];
  };
  const term = (b: number, t: number, withWeight = false): CohortSourceTerm | null => {
    const v = at(b, t);
    if (!isNum(v)) return null;
    const j = byBirth[b];
    return withWeight ? { j, t, rate: v, weight: weight(j) } : { j, t, rate: v };
  };

  const evaluate = (j: number, t: number, rule: CohortInputRule, src: CohortCellSource | null): number | null => {
    const b = birth[j];
    const count = rule.n || 1;
    switch (rule.method) {
      case 'prev': {
        const p = term(b - count, t - count);
        if (p && src) src.terms.push(p);
        return p ? p.rate : null;
      }
      case 'avg': {
        let sum = 0, used = 0;
        for (let k = 1; k <= count; k++) {
          const p = term(b - k, t - k);
          if (!p) continue;
          sum += p.rate;
          used++;
          if (src) src.terms.push(p);
        }
        return used ? sum / used : null;
      }
      case 'wavg': {
        let sum = 0, w = 0;
        for (let k = 1; k <= count; k++) {
          const p = term(b - k, t - k, true);
          if (!p) continue;
          sum += (p.weight as number) * p.rate;
          w += p.weight as number;
          if (src) src.terms.push(p);
        }
        return w ? sum / w : null;
      }
      case 'chain': {
        const own = rates[j][t - 1];
        if (!isNum(own)) return null;
        const f = chainFactor(t - b, count);
        if (src) {
          src.terms.push({ j, t: t - 1, rate: own });
          src.factor = f;
        }
        return own * (f ? f.value : 1);
      }
      case 'seasonal': {
        const base = term(b - SEASON, t - SEASON);
        if (!base) return null;
        let num = 0, den = 0, pairs = 0;
        const cells: CohortCellRef[] = [];
        for (let k = 1; k <= count; k++) {
          const recent = at(b - k, t - k);
          const prior = at(b - SEASON - k, t - SEASON - k);
          if (!isNum(recent) || !isNum(prior)) continue;
          num += recent;
          den += prior;
          pairs++;
          cells.push({ j: byBirth[b - k], t: t - k }, { j: byBirth[b - SEASON - k], t: t - SEASON - k });
        }
        const raw = den > 0 ? num / den : 1;
        const trend = den > 0 ? Math.max(TREND_MIN, Math.min(TREND_MAX, raw)) : 1;
        if (src) {
          src.terms.push(base);
          src.trend = den > 0 ? { value: trend, num, den, pairs, capped: trend !== raw, cells } : null;
        }
        return base.rate * trend;
      }
      default:
        return isNum(rule.value) ? rule.value : null;
    }
  };

  const sources: Array<Array<CohortCellSource | null>> | undefined = input.explain ? cohorts.map(() => new Array(n).fill(null)) : undefined;

  for (let t = Math.max(0, cut + 1); t < n; t++) {
    for (let j = 0; j < J; j++) {
      if (kinds[j][t] !== null) continue;
      if (isPre[j]) {
        for (let x = t - 1; x >= 0; x--) {
          if (!isNum(rates[j][x])) continue;
          rates[j][t] = rates[j][x];
          kinds[j][t] = 'rule';
          if (sources) sources[j][t] = { method: 'carry', terms: [{ j, t: x, rate: rates[j][x] as number }], result: rates[j][t] as number };
          break;
        }
        continue;
      }
      if (birth[j] < 0 || t <= birth[j]) continue;
      const rule = ruleFor(String(cohorts[j].label));
      const src: CohortCellSource | null = sources ? { method: rule.method, rule, terms: [], result: 0 } : null;
      const v = evaluate(j, t, rule, src);
      if (isNum(v)) {
        rates[j][t] = Math.max(0, v);
        kinds[j][t] = 'rule';
        if (sources && src) { src.result = rates[j][t] as number; sources[j][t] = src; }
        continue;
      }
      const fb = curvePoint(t - birth[j]);
      if (fb) {
        rates[j][t] = Math.max(0, fb.value);
        kinds[j][t] = 'fallback';
        if (sources) sources[j][t] = { method: 'fallback', rule, terms: [], curve: { age: fb.age, cohorts: fb.cohorts, cells: fb.cells }, result: rates[j][t] as number };
      }
    }
  }

  return sources ? { rates, kinds, sources } : { rates, kinds };
}
